import moment, { type Moment } from "moment";
import type { InjectionKey } from "vue";
import { arrayLast, hasValue, objectFromKeys, type TranslationKey, sum, inlineSwitch, groupBy, clampNorm } from "~/helpers/common-helpers";
import { capitalizeAll, dateRange } from "~/helpers/filter-helpers";
import type { DateRangeConfig, NamespaceOpex, NamespacePropertyOpex, PeriodRangeType } from "~/helpers/user-preferences-helpers";
import { getDateConfigRange, runTree, type AccountRow, type BalanceDateRange } from "~/helpers/modules/accounts-helpers";
import { type Category, getPropertyArea, type Property } from "~/helpers/entities/property-helpers";
import { getDateRange, getDateRangeEntries, type CategoryCell } from "~/helpers/modules/accounts-helpers";

import type { TabConfig } from "~/helpers/tabs-helpers";
import type { CategoryBudgetCell } from "~/helpers/budgets-helpers";
import type { Currency } from "~/helpers/apollo/types";
import type { UIColor } from "@/lib/design-system";
import { MetricPeriodType } from "~/graphql/generated/graphql";
import { type color, mix, mul } from "~/helpers/color-helpers";
import type { TableHeaderConfig, TableHeaderConfigMultiple } from "~/helpers/datatable-helpers";

export type ModuleOpexProperty = Property & { currentCells: CategoryCell[]; referenceCells?: CategoryCell[] };

export const getPeriodLabel = (columns: Moment[], periodRangeType: NamespaceOpex["periodRangeType"]) => {
  const periodFrom = columns[0];
  const periodTo = arrayLast(columns);
  if (!periodFrom || !periodTo) return "";

  switch (periodRangeType) {
    case "yearRolling":
      return capitalizeAll(
        `${dateRange(moment.utc(periodFrom).startOf("month").format("YYYY-MM-DD"), moment.utc(periodTo).endOf("month").format("YYYY-MM-DD"))}`
      );
    case "yearToDate":
      return capitalizeAll(
        `${dateRange(moment.utc(periodFrom).startOf("month").format("YYYY-MM-DD"), moment.utc(periodTo).endOf("month").format("YYYY-MM-DD"))}`
      );
    case "yearWhole":
      return `${dateRange(moment.utc(periodFrom).startOf("year").format("YYYY-MM-DD"), moment.utc(periodTo).endOf("year").format("YYYY-MM-DD"))}`;
    case "quarterRolling":
      return capitalizeAll(
        `${dateRange(moment.utc(periodFrom).startOf("month").format("YYYY-MM-DD"), moment.utc(periodTo).endOf("month").format("YYYY-MM-DD"))}`
      );
    case "quarterWhole":
      return `${dateRange(moment.utc(periodFrom).startOf("quarter").format("YYYY-MM-DD"), moment.utc(periodTo).endOf("quarter").format("YYYY-MM-DD"))}`;
  }
};

export const OpexSettingsInjectionKey = Symbol("opexSettingsInjectionKey") as InjectionKey<{ preferencesClone: NamespaceOpex }>;
export const PropertyOpexSettingsInjectionKey = Symbol("opexSettingsInjectionKey") as InjectionKey<{ preferencesClone: NamespacePropertyOpex }>;

export const periodRangeOptions = [
  { id: "yearWhole", name: "PORTFOLIO_VIEW_OPEX_PERIOD_WHOLE_YEAR" },
  { id: "yearRolling", name: "PORTFOLIO_VIEW_OPEX_PERIOD_LATEST_12" },
  { id: "yearToDate", name: "PORTFOLIO_VIEW_OPEX_PERIOD_YEAR_TO_DATE" },
  { id: "quarterWhole", name: "PORTFOLIO_VIEW_OPEX_PERIOD_WHOLE_QUARTER" },
  { id: "quarterRolling", name: "PORTFOLIO_VIEW_OPEX_PERIOD_LATEST_3" },
] as { id: NamespaceOpex["periodRangeType"]; name: TranslationKey }[];

export type OpexSettings = Omit<NamespaceOpex, "template" | "templateModified" | "savedTemplates">;

export type OpexSettingsTemplate = {
  id: string;
  name: string;
  settings: OpexSettings;
};

export const opexSavedSettingsFields = [
  "type",
  "benchmark",
  "excludePropertyIds",
  "includeZeroRows",
  "normaliseBy",
  "periodOffset",
  "periodRangeType",
  "referenceMonthOffset",
  "referencePercentage",
  "annualized",
  "includeCategories",
  "includePortfolioIds",
] as const satisfies (keyof OpexSettings)[];

export const settingsTemplateOptions = [
  {
    id: "normLatest12",
    name: "normLatest12",
    settings: {
      type: "preferencesNamespaceOpex",
      benchmark: "average.total",
      excludePropertyIds: ["companies"],
      includeZeroRows: false,
      normaliseBy: "area",
      periodOffset: 0,
      periodRangeType: "yearRolling",
      referenceMonthOffset: undefined,
      referencePercentage: false,
      annualized: false,
      includeCategories: undefined,
      includePortfolioIds: undefined,
    },
  },

  {
    id: "normLatestYear",
    name: "normLatestYear",
    settings: {
      type: "preferencesNamespaceOpex",
      benchmark: "average.total",
      excludePropertyIds: [],
      includeZeroRows: false,
      normaliseBy: "area",
      periodOffset: 0,
      periodRangeType: "yearWhole",
      referenceMonthOffset: undefined,
      referencePercentage: false,
      annualized: false,
      includeCategories: undefined,
      includePortfolioIds: undefined,
    },
  },

  {
    id: "normDevelopment12Percentage",
    name: "normDevelopment12Percentage",
    settings: {
      type: "preferencesNamespaceOpex",
      benchmark: "comparison.averageTotal",
      excludePropertyIds: ["companies"],
      includeZeroRows: false,
      normaliseBy: "area",
      periodOffset: 0,
      periodRangeType: "yearRolling",
      referenceMonthOffset: -12,
      referencePercentage: true,
      annualized: false,
      includeCategories: undefined,
      includePortfolioIds: undefined,
    },
  },

  {
    id: "budgetYTD",
    name: "budgetYTD",
    settings: {
      type: "preferencesNamespaceOpex",
      benchmark: "budget.diff",
      excludePropertyIds: ["companies"],
      includeZeroRows: false,
      normaliseBy: "area",
      periodOffset: 0,
      periodRangeType: "yearToDate",
      referenceMonthOffset: undefined,
      referencePercentage: false,
      annualized: false,
      includeCategories: undefined,
      includePortfolioIds: undefined,
    },
  },
] as const satisfies OpexSettingsTemplate[];

export type StandardOpexTemplateId = (typeof settingsTemplateOptions)[number]["id"];

export const OPEX_TEMPLATE_DEFAULT = "normLatest12" as const satisfies StandardOpexTemplateId;

export const getSelectedTemplate = (preferences: Nullable<NamespaceOpex>) =>
  preferences?.savedTemplates.find((template) => template.id === preferences.template) ??
  settingsTemplateOptions.find((template) => template.id === preferences?.template);

export const settingsTemplateOptionsTranslationKeys = {
  budgetYTD: "PORTFOLIO_OPEX_TEMPLATE_BUDGET_YTD",
  normLatest12: "PORTFOLIO_OPEX_TEMPLATE_NORM_ROLLING",
  normLatestYear: "PORTFOLIO_OPEX_TEMPLATE_NORM_WHOLE",
  normDevelopment12Percentage: "PORTFOLIO_OPEX_TEMPLATE_NORM_DEVELOPMENT_12_PERCENTAGE",
} as const satisfies { [k in StandardOpexTemplateId]: TranslationKey };

export const sanitizeOpexSettings = (preferences: OpexSettings | NamespaceOpex): OpexSettings => objectFromKeys(preferences, ...opexSavedSettingsFields);

export const getDateRangeConfigColumns = (preferences: DateRangeConfig) => {
  const dateRange = getDateRange(preferences.dateRange, preferences.periodType, preferences.customDateStart, preferences.customDateEnd);

  return getDateRangeEntries(dateRange, preferences.periodType);
};

export const benchmarkOptionGroups = [
  {
    id: "average.total",
    name: "PORTFOLIO_VIEW_OPEX_TABLE_STANDARD_NO",
    description: "PORTFOLIO_VIEW_OPEX_DESCRIPTION_AVG_TOTAL",
  },

  {
    id: "budget.diff",
    name: "PORTFOLIO_VIEW_OPEX_TABLE_BUDGET",
    description: "PORTFOLIO_VIEW_OPEX_DESCRIPTION_BUDGET_DIFF",
  },

  {
    id: "comparison.averageTotal",
    name: "PORTFOLIO_VIEW_OPEX_TABLE_COMPARISON",
    description: "PORTFOLIO_VIEW_OPEX_DESCRIPTION_AVG_TOTAL_COMPARISON",
  },
] as const satisfies { id: string; name: TranslationKey; description: TranslationKey }[];

export const isStandardSetting = (settings: OpexSettingsTemplate): settings is (typeof settingsTemplateOptions)[number] =>
  !!settingsTemplateOptions.find((g) => g.id === settings.id);

export type BenchmarkGroup = (typeof benchmarkOptionGroups)[number];
export type BenchmarkGroupOption = BenchmarkGroup["id"];

export const getDiff = (target: number, benchmark: number) => target - benchmark;

export const getDiffPercentage = (target: number, benchmark: number, safeDecimals = 3) => {
  const roundingFactor = Math.pow(10, safeDecimals);
  target = Math.round(target * roundingFactor) / roundingFactor;
  benchmark = Math.round(benchmark * roundingFactor) / roundingFactor;

  const diff = getDiff(target, benchmark);

  return Math.abs(target) === 0 && Math.abs(benchmark) === 0 ? 0 : Math.sign(diff) * Math.abs(diff / benchmark) * 100;
};

export type OpexAccountDataObject = { current: AccountRow; reference: AccountRow };

export const referenceSet = (preferences: OpexSettings) => preferences.benchmark === "comparison.averageTotal" && hasValue(preferences.referenceMonthOffset);
export const budgetSet = (preferences: OpexSettings) => preferences.benchmark === "budget.diff";

export type OpexSettingsModalTab = "general" | "categories";
export const opexSettingsModalTabs: { [k in OpexSettingsModalTab]: TabConfig<k> } = {
  general: {
    labelKey: "PORTFOLIO_BALANCE_SETTINGS_GENERAL_HEADER",
    tabKey: "general",
    default: true,
  },

  categories: {
    labelKey: "PORTFOLIO_DATA_WAREHOUSE_MENU_CATALOGUE_CATEGORIES",
    tabKey: "categories",
  },
};

export type PropertyOpexTree = {
  id: string;
  name: string;
  parent: PropertyOpexTree | undefined;
  children: PropertyOpexTree[];
  type: PropertyOpexRow["type"];
};

export type PropertyOpexColumnValue = {
  total: number;
  reference12m: number;
  reference24m: number;
  avg5y: number;
  portfolio: number;
  budget: number;
};

export type PropertyOpexRow = {
  type: "sum" | "category";
  id: string;
  name: string;
  columnValues: PropertyOpexColumnValue;
  children: PropertyOpexRow[];
};

export type ModuleOpexColumnValueStandard = {
  displayValue: number;
  diff: number;
  diffPercentage: number;
  realized: number;
  norm: number;
  entities: number;
  annualizationFactor: number;
};

export type ModuleOpexColumnValueBudget = {
  displayValue: number;
  diff: number;
  diffPercentage: number;
  realized: number;
  budgeted: number;
  norm: number;
  entities: number;
  annualizationFactor: number;
};

export type ModuleOpexColumnValueReference = {
  displayValue: number;
  diff: number;
  diffPercentage: number;

  current: {
    realized: number;
    norm: number;
    entities: number;
    annualizationFactor: number;
  };
  reference: {
    realized: number;
    norm: number;
    entities: number;
    annualizationFactor: number;
  };
};

export interface ModuleOpexRowBase {
  rowType: "sum" | "category";
  dataType: BenchmarkGroupOption;
  id: string;
  name: string;
}

export interface ModuleOpexRowStandard extends ModuleOpexRowBase {
  dataType: "average.total";
  children: ModuleOpexRowStandard[];
  averageColumnValue: ModuleOpexColumnValueStandard;
  columnValues: ModuleOpexColumnValueStandard[];
}

export interface ModuleOpexRowBudget extends ModuleOpexRowBase {
  dataType: "budget.diff";
  children: ModuleOpexRowBudget[];
  averageColumnValue: ModuleOpexColumnValueBudget;
  columnValues: ModuleOpexColumnValueBudget[];
}

export interface ModuleOpexRowReference extends ModuleOpexRowBase {
  dataType: "comparison.averageTotal";
  children: ModuleOpexRowReference[];
  averageColumnValue: ModuleOpexColumnValueReference;
  columnValues: ModuleOpexColumnValueReference[];
}

export type ModuleOpexRowDataType = ModuleOpexRow["dataType"];

export type ModuleOpexRow = ModuleOpexRowStandard | ModuleOpexRowBudget | ModuleOpexRowReference;

export const getModuleOpexCurrency = (properties: ModuleOpexProperty[]) =>
  properties.flatMap((p) => p.currentCells).find((cell) => !!cell.currency)?.currency as Currency | undefined;

export const createPropertyOpexCategoryRow = (category: Category, parent: PropertyOpexTree | undefined, children: PropertyOpexTree[]): PropertyOpexTree => ({
  id: category.id,
  name: category.name ?? "",
  type: "category",
  parent,
  children,
});

export const createModuleOpexRow = <R extends ModuleOpexRow>(tree: PropertyOpexTree, children: R[], benchmark: BenchmarkGroupOption): R => {
  switch (benchmark) {
    case "average.total":
      return (<ModuleOpexRowStandard>{
        id: tree.id,
        name: tree.name,
        rowType: tree.type,
        dataType: "average.total",
        averageColumnValue: {
          realized: 0,
          displayValue: 0,
          diff: 0,
          diffPercentage: 0,
          norm: 0,
          entities: 0,
          annualizationFactor: 1,
        },
        columnValues: [],
        children,
      }) as R;
    case "budget.diff":
      return (<ModuleOpexRowBudget>{
        id: tree.id,
        name: tree.name,
        rowType: tree.type,
        dataType: "budget.diff",
        averageColumnValue: {
          realized: 0,
          budgeted: 0,
          displayValue: 0,
          diff: 0,
          diffPercentage: 0,
          norm: 0,
          entities: 0,
          annualizationFactor: 1,
        },
        columnValues: [],
        children,
      }) as R;
    case "comparison.averageTotal":
      return (<ModuleOpexRowReference>{
        id: tree.id,
        name: tree.name,
        rowType: tree.type,
        dataType: "comparison.averageTotal",
        averageColumnValue: {
          displayValue: 0,
          diff: 0,
          diffPercentage: 0,
          current: {
            entities: 0,
            norm: 0,
            realized: 0,
            annualizationFactor: 1,
          },
          reference: {
            entities: 0,
            norm: 0,
            realized: 0,
            annualizationFactor: 1,
          },
        },
        columnValues: [],
        children,
      }) as R;
  }
};

export const isZeroRow = (row: ModuleOpexRow) => {
  const isZeroValue = (v: number) => isNaN(v) || Math.round(v * 1000) / 1000 === 0;

  switch (row.dataType) {
    case "average.total":
      return row.columnValues.every((v) => isZeroValue(v.realized)) && isZeroValue(row.averageColumnValue.realized);
    case "budget.diff":
      return (
        row.columnValues.every((v) => isZeroValue(v.realized) && isZeroValue(v.budgeted)) &&
        isZeroValue(row.averageColumnValue.realized) &&
        isZeroValue(row.averageColumnValue.budgeted)
      );
    case "comparison.averageTotal": {
      return (
        row.columnValues.every((v) => isZeroValue(v.current.realized) && isZeroValue(v.reference.realized)) &&
        isZeroValue(row.averageColumnValue.current.realized) &&
        isZeroValue(row.averageColumnValue.reference.realized)
      );
    }
  }
};

export const propertyOpexColumns = ["reference12m", "reference24m", "5yearAvg", "portfolio", "budget"] as const;
export type PropertyOpexColumn = (typeof propertyOpexColumns)[number];

export type OpexSumRow = DataItem & { type: "sum"; temp: boolean };
export type OpexRowItem = DataItem | OpexSumRow;

export const getSumRowId = (row: { name: string; temp: boolean }) => `${row.temp ? "_temp" : ""}_sum_${row.name}`;
export const rowIsSum = (item: OpexRowItem): item is OpexSumRow => "type" in item && item.type === "sum";

export const getPropertyNormalizeValue = (property: Property, normalizeBy: OpexSettings["normaliseBy"]) =>
  normalizeBy === "area" ? getPropertyArea(property).area : normalizeBy === "tenancies" ? property.assetManagementTenancies?.metadata?.totalCount ?? 0 : 1;

export const setModuleOpexRowsStandardData = (
  rows: ModuleOpexRowStandard[],
  columns: ModuleOpexProperty[],
  normalizeBy: OpexSettings["normaliseBy"],
  annualized: boolean,
  periodRange?: DateRange
) => {
  const annualizationFactor = annualized && periodRange ? annualizeTimeRange(periodRange) : 1;

  const resultsCells = columns.flatMap((c) => c.currentCells.map((cell) => ({ propertyId: c.id, categoryId: cell.categoryId, value: cell.totalCostWithVat })));
  const resultsCellsGroups = groupBy(resultsCells, {
    categoryId: { type: "string", getKey: (cell) => cell.categoryId },
    propertyId: { type: "string", getKey: (cell) => cell.propertyId },
  });

  for (let row of rows) {
    runTree(row, (row) => {
      row.averageColumnValue = {
        diff: 0,
        diffPercentage: 0,
        displayValue: 0,
        entities: columns.length,
        norm: 0,
        realized: 0,
        annualizationFactor,
      };

      row.columnValues = columns.map((column, index) => {
        const realizedChildren = sum(row.children.map((c) => c.columnValues[index].realized));

        const realizedSelf = sum(resultsCellsGroups.get({ categoryId: row.id, propertyId: column.id })?.items.map((cell) => cell.value) ?? []);

        let realizedTotal = realizedSelf + realizedChildren;

        const norm = getPropertyNormalizeValue(column, normalizeBy);

        row.averageColumnValue.norm += norm;
        row.averageColumnValue.realized += realizedTotal;

        return {
          realized: realizedTotal,
          displayValue: (realizedTotal * annualizationFactor) / (norm || 1),
          norm,
          entities: 1,
          diff: 0,
          diffPercentage: 0,
          annualizationFactor,
        };
      });

      row.averageColumnValue.displayValue = (row.averageColumnValue.realized * annualizationFactor) / (row.averageColumnValue.norm || 1);

      row.columnValues.forEach((v) => {
        const target = v.realized / (v.norm || 1);
        const benchmark = row.averageColumnValue.realized / (row.averageColumnValue.norm || 1);

        v.diff = getDiff(target, benchmark);
        v.diffPercentage = getDiffPercentage(target, benchmark);
      });
    });
  }
};

export const setModuleOpexRowsBudgetData = (
  rows: ModuleOpexRowBudget[],
  columns: ModuleOpexProperty[],
  budgetCells: CategoryBudgetCell[],
  normalizeBy: OpexSettings["normaliseBy"],
  annualized: boolean,
  periodRange?: DateRange
) => {
  const resultsCells = columns.flatMap((c) => c.currentCells.map((cell) => ({ propertyId: c.id, categoryId: cell.categoryId, value: cell.totalCostWithVat })));
  const resultsCellsGroups = groupBy(resultsCells, {
    categoryId: { type: "string", getKey: (cell) => cell.categoryId },
    propertyId: { type: "string", getKey: (cell) => cell.propertyId },
  });

  const budgetCellsGroups = groupBy(budgetCells, {
    categoryId: { getKey: (cell) => cell.categoryId, type: "string" },
    propertyId: { getKey: (cell) => cell.propertyId, type: "string" },
  });

  const annualizationFactor = annualized && periodRange ? annualizeTimeRange(periodRange) : 1;

  for (let row of rows) {
    runTree(row, (row) => {
      row.averageColumnValue = {
        realized: 0,
        budgeted: 0,
        displayValue: 0,
        entities: columns.length,
        norm: 0,
        diff: 0,
        diffPercentage: 0,
        annualizationFactor,
      };

      row.columnValues = columns.map((column: ModuleOpexProperty, index: number) => {
        const realizedChildren = sum(row.children.map((c) => c.columnValues[index].realized));
        const budgetedChildren = sum(row.children.map((c) => c.columnValues[index].budgeted));

        const realizedSelf = sum(resultsCellsGroups.get({ categoryId: row.id, propertyId: column.id })?.items.map((cell) => cell.value) ?? []);
        const budgetedSelf = sum(budgetCellsGroups.get({ categoryId: row.id, propertyId: column.id })?.items.map((cell) => cell.value) ?? []);

        let realizedTotal = realizedSelf + realizedChildren;
        let budgetedTotal = budgetedSelf + budgetedChildren;

        const norm = getPropertyNormalizeValue(column, normalizeBy);

        const diff = getDiff(realizedTotal / (norm || 1), budgetedTotal / (norm || 1));

        row.averageColumnValue.norm += norm;
        row.averageColumnValue.realized += realizedTotal;
        row.averageColumnValue.budgeted += budgetedTotal;

        return {
          realized: realizedTotal,
          budgeted: budgetedTotal,
          displayValue: diff * annualizationFactor,
          entities: 1,
          norm,
          diff,
          diffPercentage: getDiffPercentage(realizedTotal / (norm || 1), budgetedTotal / (norm || 1)),
          annualizationFactor,
        };
      });

      const diff = getDiff(
        row.averageColumnValue.realized / (row.averageColumnValue.norm || 1),
        row.averageColumnValue.budgeted / (row.averageColumnValue.norm || 1)
      );

      row.averageColumnValue.displayValue = diff * annualizationFactor;
      row.averageColumnValue.diff = diff;
      row.averageColumnValue.diffPercentage = getDiffPercentage(row.averageColumnValue.realized, row.averageColumnValue.budgeted);
    });
  }
};

export const setModuleOpexRowsReferenceData = (
  rows: ModuleOpexRowReference[],
  columns: ModuleOpexProperty[],
  normalizeBy: OpexSettings["normaliseBy"],
  showAsPercentage: boolean,
  annualized: boolean,
  periodRangeCurrent?: DateRange,
  periodRangeReference?: DateRange
) => {
  const resultsCellsCurrent = columns.flatMap((c) =>
    c.currentCells.map((cell) => ({ propertyId: c.id, categoryId: cell.categoryId, value: cell.totalCostWithVat }))
  );
  const resultsCellsReference = columns.flatMap(
    (c) => c.referenceCells?.map((cell) => ({ propertyId: c.id, categoryId: cell.categoryId, value: cell.totalCostWithVat })) ?? []
  );

  const resultsCellsGroupsCurrent = groupBy(resultsCellsCurrent, {
    categoryId: { type: "string", getKey: (cell) => cell.categoryId },
    propertyId: { type: "string", getKey: (cell) => cell.propertyId },
  });
  const resultsCellsGroupsReference = groupBy(resultsCellsReference, {
    categoryId: { type: "string", getKey: (cell) => cell.categoryId },
    propertyId: { type: "string", getKey: (cell) => cell.propertyId },
  });

  const annualizationFactorCurrent = annualized && periodRangeCurrent ? annualizeTimeRange(periodRangeCurrent) : 1;
  const annualizationFactorReference = annualized && periodRangeReference ? annualizeTimeRange(periodRangeReference) : 1;

  const getColumnValue = (currentRealized: number, referenceRealized: number, norm: number, entities = 1): ModuleOpexColumnValueReference => {
    const diff = getDiff(currentRealized, referenceRealized) / (norm || 1);
    const diffPercentage = getDiffPercentage(currentRealized, referenceRealized);
    const currentRealizedDisplay = currentRealized * annualizationFactorCurrent;
    const referenceRealizedDisplay = referenceRealized * annualizationFactorReference;

    const displayDiff = showAsPercentage
      ? getDiffPercentage(currentRealizedDisplay, referenceRealizedDisplay)
      : getDiff(currentRealizedDisplay, referenceRealizedDisplay) / (norm || 1);

    return {
      displayValue: -1 * displayDiff,
      diff,
      diffPercentage,

      current: {
        realized: currentRealized,
        entities,
        norm: norm,
        annualizationFactor: annualizationFactorCurrent,
      },
      reference: {
        realized: referenceRealized,
        entities,
        norm: norm,
        annualizationFactor: annualizationFactorReference,
      },
    };
  };

  for (let row of rows) {
    runTree(row, (row) => {
      let totalNorm = 0;
      let totalCurrent = 0;
      let totalReference = 0;

      row.columnValues = columns.map((column, index) => {
        const currentRealizedChildren = sum(row.children.map((c) => c.columnValues[index].current.realized));
        const referenceRealizedChildren = sum(row.children.map((c) => c.columnValues[index].reference.realized));

        const currentRealizedSelf = sum(resultsCellsGroupsCurrent.get({ categoryId: row.id, propertyId: column.id })?.items.map((cell) => cell.value) ?? []);
        const referenceRealizedSelf = sum(
          resultsCellsGroupsReference.get({ categoryId: row.id, propertyId: column.id })?.items.map((cell) => cell.value) ?? []
        );

        let currentRealizedTotal = currentRealizedSelf + currentRealizedChildren;
        let referenceRealizedTotal = referenceRealizedSelf + referenceRealizedChildren;

        const norm = getPropertyNormalizeValue(column, normalizeBy);

        totalNorm += norm;
        totalCurrent += currentRealizedTotal;
        totalReference += referenceRealizedTotal;

        return getColumnValue(currentRealizedTotal, referenceRealizedTotal, norm);
      });

      row.averageColumnValue = getColumnValue(totalCurrent, totalReference, totalNorm, columns.length);
    });
  }
};

export const getCutoffs = (benchmark: BenchmarkGroupOption): [number, number, number] => {
  switch (benchmark) {
    case "budget.diff":
      return [-0.05, 0, 0.05];
    case "comparison.averageTotal":
    case "average.total":
      return [-1, 0, 2];
  }
};

export const getColorFractions = (row: ModuleOpexRow, index: number, cutoffs: [number, number, number]): [number, number, number] | undefined => {
  let value = row.columnValues[index].diffPercentage;

  if (!hasValue(value) || isNaN(value)) return;

  value = (-1 * value) / 100;

  return [
    1 - clampNorm(cutoffs[0], cutoffs[1], value),
    clampNorm(cutoffs[0], cutoffs[1], value) - clampNorm(cutoffs[1], cutoffs[2], value),
    clampNorm(cutoffs[1], cutoffs[2], value),
  ];
};

export const getRowCopyColor = (row: ModuleOpexRow, index: number, benchmark: BenchmarkGroupOption) => {
  const isBudget = benchmark === "budget.diff";

  const cutoffs = getCutoffs(benchmark);

  const [greenF, yellowF, redF] = getColorFractions(row, index, cutoffs) ?? (isBudget ? [0, 1, 0] : [1, 0, 0]);

  const dominantColor = greenF > yellowF && greenF > redF ? "green" : yellowF > redF ? "yellow" : "red";

  return `--color-${dominantColor}-1000` as UIColor;
};

export const getRowCellColor = (row: ModuleOpexRow, index: number, benchmark: BenchmarkGroupOption) => {
  const green = { r: 221, g: 244, b: 232 }; //--color-green-100
  const yellow = { r: 251, g: 236, b: 195 }; //--color-yellow-100
  const red = { r: 255, g: 202, b: 202 }; //--color-red-100

  const toRGB = (color: color) => `rgb(${color.r},${color.g},${color.b})`;

  const isBudget = benchmark === "budget.diff";

  const cutoffs = getCutoffs(benchmark);

  const [greenF, yellowF, redF] = getColorFractions(row, index, cutoffs) ?? (isBudget ? [0, 1, 0] : [1, 0, 0]);

  const color = mix(mul(green, greenF), mul(yellow, yellowF), mul(red, redF));

  return { rgb: toRGB(color) };
};

export const getOpexPeriodConfig = (
  preferences: { periodOffset: number; periodRangeType: PeriodRangeType },
  options: { monthOffset?: number; details?: boolean } = {}
) => {
  const periodType = MetricPeriodType.Monthly;

  const dateRange = inlineSwitch(preferences.periodRangeType, {
    yearWhole: () =>
      getDateConfigRange({
        customDateStart: moment.utc().startOf("year").subtract(1, "y").format("YYYY-MM-DD"),
        customDateEnd: moment.utc().endOf("year").subtract(1, "y").format("YYYY-MM-DD"),
        dateRange: "custom",
        periodType: periodType,
      }),

    yearRolling: () =>
      getDateConfigRange({
        customDateEnd: undefined,
        customDateStart: undefined,
        dateRange: "latest12",
        periodType: periodType,
      }),

    yearToDate: () => {
      const start = moment.utc().startOf("year");
      let end = moment.utc().subtract(1, "month");

      if (end.isBefore(start)) end = moment.utc(start);

      return getDateConfigRange({
        customDateStart: start.format("YYYY-MM-DD"),
        customDateEnd: end.format("YYYY-MM-DD"),
        dateRange: "custom",
        periodType: periodType,
      });
    },

    quarterWhole: () =>
      getDateConfigRange({
        customDateStart: moment.utc().startOf("quarter").subtract(1, "quarter").format("YYYY-MM-DD"),
        customDateEnd: moment.utc().endOf("quarter").subtract(1, "quarter").format("YYYY-MM-DD"),
        dateRange: "custom",
        periodType: periodType,
      }),

    quarterRolling: () => {
      const start = moment.utc().subtract(3, "months").startOf("month");
      const end = moment.utc().subtract(1, "month").endOf("month");

      return getDateConfigRange({
        customDateStart: start.format("YYYY-MM-DD"),
        customDateEnd: end.format("YYYY-MM-DD"),
        dateRange: "custom",
        periodType: periodType,
      });
    },
  });

  const offsetDate = (date: Moment) => {
    const monthOffset = options.monthOffset;
    if (hasValue(monthOffset)) date.add(monthOffset, "months");

    switch (preferences.periodRangeType) {
      case "yearWhole":
        return moment.utc(date).add(preferences.periodOffset, "y");
      case "quarterWhole":
        return moment.utc(date).add(preferences.periodOffset, "quarter");
      case "yearRolling":
      case "yearToDate":
      case "quarterRolling":
        return date;
    }
  };

  const from = offsetDate(dateRange.from);
  const to = offsetDate(dateRange.to);
  const columns = getDateRangeEntries({ from: from, to: to }, periodType);
  const label = getPeriodLabel(columns, preferences.periodRangeType);

  return {
    from,
    to,
    columns,
    label,
    periodType,
  };
};

export type DateRange = { from: Moment; to: Moment };

export const annualizeTimeRange = ({ from, to }: DateRange): number => {
  // Add 1 to include the starting month in the count
  const diff = to.diff(from, "months") + 1;

  return diff === 0 ? 12 : 12 / Math.abs(diff);
};

export const flattenOpexHeaders = (headers: TableHeaderConfigMultiple<ModuleOpexRow>[]) => {
  const flattened: TableHeaderConfig<ModuleOpexRow>[] = [];

  headers.forEach((multiHeader) => {
    multiHeader.headers.values.forEach((valueConfig) => {
      // Extract cell color function if available
      const cellColorFn = valueConfig.dataTableCellOptions?.cellColor;

      flattened.push({
        id: `${multiHeader.id}_${valueConfig.id}`,
        labelKey: multiHeader.headers.title.labelKey,
        label: multiHeader.headers.title.label,
        textAlignEnd: multiHeader.headers.title.textAlignEnd,
        value: valueConfig.value,
        export: valueConfig.export,
      } as TableHeaderConfig<ModuleOpexRow>);
    });
  });

  return flattened;
};
